import { css } from '@emotion/react';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { getKnownSourceObjects, getLatestTemplate } from '../../../services/queries/AtlasQueries';
import {
  SOURCE_OBJECT_PATTERN,
  atlasGlUploadOptionsSchema,
} from '../../../schemas/AtlasGlUploadOptions.schema';

/** Backend default; preserved here so an operator who never touches the
 *  selector still gets the historical wire value. */
const DEFAULT_SOURCE_OBJECT = 'generalLedger';

/** localStorage key for the last-used "selected from existing" sourceObject.
 *  Scoped to the GL upload form — other source pickers (if added later) get
 *  their own key. We persist only the last selected source so newly added
 *  files inherit it; we do not remember per-file historical selections. */
const LAST_SOURCE_STORAGE_KEY = 'atlas.gl.upload.lastSourceObject';

/** Sentinel value used in the Select to mean "let me type a new one". MUI's
 *  Select requires a non-empty string value to render the underlying option;
 *  this string can't collide with a real sourceObject because '+' is not in
 *  SOURCE_OBJECT_PATTERN. */
const NEW_SOURCE_SENTINEL = '__new__';

export interface AtlasUploadOptionsValue {
  /** Resolved source name (either selected-from-existing or newly typed). */
  sourceObject: string;
  /** ISO YYYY-MM-DD; '' when blank. */
  coverageStart: string;
  /** ISO YYYY-MM-DD; '' when blank. */
  coverageEnd: string;
}

interface AtlasUploadOptionsErrors {
  sourceObject?: string;
  coverageStart?: string;
  coverageEnd?: string;
}

/**
 * Reads `localStorage.atlas.gl.upload.lastSourceObject`. Guards against the
 * (rare) case where storage is unavailable — e.g. the operator is using a
 * private window with strict storage — so the form still renders.
 */
function readLastSource(): string | null {
  try {
    const raw = window.localStorage.getItem(LAST_SOURCE_STORAGE_KEY);
    // Legacy default — operators with stale localStorage from the old 'gl'
    // default shouldn't get dropped into "+ New…" mode prefilled with 'gl';
    // let DEFAULT_SOURCE_OBJECT take over.
    if (raw === 'gl') {
      return null;
    }
    if (raw && SOURCE_OBJECT_PATTERN.test(raw)) {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

function writeLastSource(name: string): void {
  try {
    window.localStorage.setItem(LAST_SOURCE_STORAGE_KEY, name);
  } catch {
    // ignore — see readLastSource
  }
}

/** Build the default value newly-added rows start with. Uses the operator's
 *  last remembered selection when present, falling back to the backend
 *  default ('generalLedger'). */
export function defaultAtlasUploadOptions(): AtlasUploadOptionsValue {
  return {
    sourceObject: readLastSource() ?? DEFAULT_SOURCE_OBJECT,
    coverageStart: '',
    coverageEnd: '',
  };
}

function validate(value: AtlasUploadOptionsValue): AtlasUploadOptionsErrors {
  try {
    atlasGlUploadOptionsSchema().validateSync(
      {
        sourceObject: value.sourceObject,
        coverageStart: value.coverageStart || undefined,
        coverageEnd: value.coverageEnd || undefined,
      },
      { abortEarly: false }
    );
    return {};
  } catch (err) {
    const acc: AtlasUploadOptionsErrors = {};
    const ve = err as { inner?: Array<{ path?: string; message: string }> };
    for (const inner of ve.inner ?? []) {
      if (inner.path && !(inner.path in acc)) {
        (acc as Record<string, string>)[inner.path] = inner.message;
      }
    }
    return acc;
  }
}

/** Returns true when the supplied opts pass the per-file schema. Centralized
 *  here so the form's "all rows valid" gate uses the same rules the inputs
 *  render. */
export function isAtlasUploadOptionsValid(value: AtlasUploadOptionsValue): boolean {
  return Object.keys(validate(value)).length === 0;
}

/** Shared hook for the per-row "existing source" dropdown. Pulls the union of
 *  past delivery sourceObjects + the latest template's declared sources from
 *  `/atlas/sourceObjects` (react-query dedupes across rows so N rows = 1
 *  request). Older backends without that endpoint fall back to the latest
 *  template alone so the picker keeps working through a rolling deploy. */
function useExistingSources(): { sources: string[]; loadFailed: boolean; isLoading: boolean } {
  const sourcesQuery = useQuery({
    queryKey: ['atlas', 'sourceObjects'],
    queryFn: async () => {
      try {
        return await getKnownSourceObjects();
      } catch (err) {
        // Older backend without /atlas/sourceObjects — derive sources from
        // the latest template so the dropdown still renders. Any non-404
        // error rethrows so the loadFailed UX kicks in.
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          try {
            const tmpl = await getLatestTemplate();
            const classifications = tmpl.sourceClassifications ?? [];
            return Array.from(new Set(classifications.map((c) => c.sourceObject))).sort();
          } catch (innerErr) {
            // No template yet (greenfield FA) — empty list, type-a-new-source UX.
            if (axios.isAxiosError(innerErr) && innerErr.response?.status === 404) {
              return [] as string[];
            }
            throw innerErr;
          }
        }
        throw err;
      }
    },
    retry: false,
  });

  return {
    sources: sourcesQuery.data ?? [],
    loadFailed: !sourcesQuery.isLoading && sourcesQuery.isError,
    isLoading: sourcesQuery.isLoading,
  };
}

interface AtlasRowSourceSelectProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/**
 * Per-row Source-object control: an "existing sources" dropdown with a
 * "+ New" sentinel that swaps in a free-text input. Picking from the dropdown
 * also writes to localStorage so subsequently added rows default to it.
 */
export function AtlasRowSourceSelect({ value, onChange, disabled }: AtlasRowSourceSelectProps) {
  const { sources, loadFailed, isLoading } = useExistingSources();
  const id = useId();

  // "+ New" toggle. Default to dropdown-mode when there are existing sources
  // and the current value matches one of them; otherwise start in free-text
  // mode (greenfield FA, or operator typed a custom name).
  const [isAddingNew, setIsAddingNew] = useState<boolean>(() => {
    return sources.length === 0 || !sources.includes(value);
  });

  // When sources load, re-sync the mode flag once: stay in select mode if the
  // value matches an existing source, otherwise reveal the free-text input.
  useEffect(() => {
    setIsAddingNew(sources.length === 0 || !sources.includes(value));
    // value is intentionally excluded — running this on every keystroke would
    // fight the user's input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources.join('|')]);

  const errors = useMemo(
    () => validate({ sourceObject: value, coverageStart: '', coverageEnd: '' }),
    [value]
  );

  const handleSelectChange = useCallback(
    (next: string) => {
      if (next === NEW_SOURCE_SENTINEL) {
        setIsAddingNew(true);
        // Clear so the text field starts empty rather than carrying the
        // previous selection into "new" mode.
        onChange('');
        return;
      }
      setIsAddingNew(false);
      writeLastSource(next);
      onChange(next);
    },
    [onChange]
  );

  const selectValue = isAddingNew || !sources.includes(value) ? NEW_SOURCE_SENTINEL : value;

  return (
    <Stack direction='row' spacing={0.5} alignItems='flex-start' css={rowSourceCss}>
      <FormControl size='small' css={rowSelectFieldCss} disabled={disabled}>
        <InputLabel id={`atlas-source-${id}`}>Source</InputLabel>
        <Select
          labelId={`atlas-source-${id}`}
          label='Source'
          value={selectValue}
          onChange={(e) => handleSelectChange(String(e.target.value))}
        >
          {sources.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
          <MenuItem value={NEW_SOURCE_SENTINEL}>+ New…</MenuItem>
        </Select>
      </FormControl>
      {!isLoading && (isAddingNew || sources.length === 0) && (
        <TextField
          size='small'
          label='New source name'
          placeholder='e.g. positions'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(errors.sourceObject) && value !== ''}
          helperText={
            value === '' ? (loadFailed ? "Couldn't load sources; type one." : '') : errors.sourceObject || ''
          }
          disabled={disabled}
          css={rowNewSourceFieldCss}
        />
      )}
    </Stack>
  );
}

interface AtlasRowCoverageInputProps {
  /** Either coverage bound — start or end. */
  value: string;
  onChange: (next: string) => void;
  /** Used to compute the cross-field range error when both bounds are set. */
  pair: { coverageStart: string; coverageEnd: string };
  field: 'coverageStart' | 'coverageEnd';
  disabled?: boolean;
}

/**
 * Per-row coverage date input. Uses native `<input type="date" />` via MUI's
 * TextField — same styling pattern the form-level fields used. Surfaces the
 * cross-field "end >= start" error inline on the end field.
 */
export function AtlasRowCoverageInput({
  value,
  onChange,
  pair,
  field,
  disabled,
}: AtlasRowCoverageInputProps) {
  const errors = useMemo(
    () =>
      validate({
        sourceObject: 'generalLedger', // not checked here; sentinel for the schema
        coverageStart: pair.coverageStart,
        coverageEnd: pair.coverageEnd,
      }),
    [pair.coverageStart, pair.coverageEnd]
  );
  return (
    <TextField
      size='small'
      type='date'
      label={field === 'coverageStart' ? 'Start' : 'End'}
      // type='date' inputs need shrunk labels to avoid overlapping the
      // empty placeholder.
      slotProps={{ inputLabel: { shrink: true } }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(errors[field])}
      helperText={errors[field] || ''}
      disabled={disabled}
      css={rowDateFieldCss}
    />
  );
}

const rowSourceCss = css`
  /* width sizes to content; the inner FormControl carries the real min-width */
`;

/* Fixed width (not min-width) so the Source dropdown renders the same size
 * across rows regardless of selected text — 'soi' and 'generalLedger' otherwise
 * yield visibly different box widths and the column reads as misaligned. */
const rowSelectFieldCss = css`
  width: 180px;
`;

const rowNewSourceFieldCss = css`
  min-width: 140px;
  max-width: 200px;
`;

const rowDateFieldCss = css`
  width: 150px;
`;
